import { useState } from "react";

import Button from "@vfuk/core-button";
import Paragraph from "@vfuk/core-paragraph/";
import Spacing from "@vfuk/core-spacing";
import StateNotification from "@vfuk/core-state-notification";

import { IconScreen } from "../IconScreen";
import {
  hasLettersNumbersSymbols,
  hasPasswordLength,
  hasUpperAndLowerCase,
} from "../utils";
import ProfileChangePasswordForm from "./ProfileChangePasswordForm";
import ReturnStep from "./ReturnStep";
import { useProfileModal } from "./hooks/useProfileModal";

const ChangePassword = () => {
  const { setView, authenticationService } = useProfileModal();

  // Error or Loading States
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Authentication Value States
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const onSubmit = async () => {
    // Set the loading state & clear the error
    setLoading(true);
    setError("");

    // Send the request to the API
    await authenticationService
      ?.changeMyPassword(password, newPassword)
      .then(({ data }) => setSuccessMessage(data.message))
      .catch(({ response }) =>
        setError(response.data.message || "An unknown error occurred.")
      )
      .finally(() => setLoading(false));
  };

  // Check whether all criteria are met before enabling the button
  const isSubmitEnabled = () =>
    hasPasswordLength(newPassword, confirmNewPassword) &&
    hasLettersNumbersSymbols(newPassword, confirmNewPassword) &&
    hasUpperAndLowerCase(newPassword, confirmNewPassword);

  return (
    <>
      <ReturnStep onBack={() => setView("EDIT_PROFILE")} />

      {successMessage ? (
        <IconScreen
          title="Password changed successfully!"
          description={successMessage}
          group="hifiLight"
          icon="tick-outline"
          buttonOnClick={() => setView("EDIT_PROFILE")}
          buttonText="Done"
          buttonAppeareance="alt1"
        />
      ) : (
        <Spacing
          spacingLevel={{
            top: 4,
          }}
        >
          {error && (
            <Spacing spacingLevel={{ bottom: 6 }}>
              <StateNotification state="error">
                <Paragraph>{error}</Paragraph>
              </StateNotification>
            </Spacing>
          )}
          <form onSubmit={(e) => e.preventDefault()}>
            <ProfileChangePasswordForm
              password={password}
              onPassword={setPassword}
              newPassword={newPassword}
              onNewPassword={setNewPassword}
              confirmNewPassword={confirmNewPassword}
              onConfirmNewPassword={setConfirmNewPassword}
            />
            <Spacing spacingLevel={{ top: 6 }}>
              <Button
                text="Submit"
                htmlAttributes={{
                  type: "submit",
                }}
                appearance="primary"
                width="full"
                onClick={onSubmit}
                loading={isLoading}
                state={isSubmitEnabled() && !isLoading ? undefined : "disabled"}
              />
            </Spacing>
          </form>
        </Spacing>
      )}
    </>
  );
};

export default ChangePassword;
